<template>
  <div class="content mb-5">
    <b-row class="mb-5">
      <b-col>
        <b-alert :show="showWarning" variant="info">
          <b-icon icon="info-circle" class="mr-3"></b-icon
          >{{
            $t(
              "Location policies are active. The actual policy for any user inheriting the Customer Default Policy will depend on the user's location."
            )
          }}
        </b-alert>
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col>
        <t-data-table
          data-test-id="users-data-table"
          with-filter
          hoverable
          :filter-placeholder="$t('Search Users')"
          :loading="loading"
          :columnsOnLoad="6"
          :title="this.$t('All Users')"
          :table-data="dataSet"
          :server-items-length="serverItemsLength"
          :actions="tableActions"
          user-role="customer"
          @on-export="onExport"
          @on-delete="onDelete"
          @on-row-clicked="onRowClicked"
          :searchExports="searchExports"
          @export-headers="exportedHeaders"
          @async-paginate="onSearch"
        />
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
  "name": "Customer Users",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  layout: "customer",
  data() {
    return {
      loading: true,
      currentHeaders: null,
      showWarning: false,
      dataOptions: {
        results: 10,
        page: 1,
        sortBy: "name",
        sortDesc: "asc",
        filter: null,
      },
      tableActions: {
        add: { enabled: false },
        export: { enabled: true },
        import: { enabled: false },
        delete: { enabled: true },
      },
      serverItemsLength: 0,
      searchExports: ["name", "description", "email", "effective_policy"],
      dataSet: {
        fields: [
          { value: "name", text: this.$t("Full Name"), sortable: true },
          { value: "email", text: this.$t("Email"), sortable: true },
          {
            value: "description",
            text: this.$t("Description"),
            sortable: true,
          },
          {
            value: "effective_policy",
            text: this.$t("Effective Policy"),
            sortable: true,
          },
          {
            value: "policy_inherited",
            text: this.$t("Policy Inherited"),
            sortable: true,
          },
        ],
        items: [],
      },
    };
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("internalUsers", [
      "fetchInternalUserAccounts",
      "deleteInternalUserAccounts",
    ]),
    onRowClicked(item) {
      this.$router.push(`/customer/environment/users/${item.id}`);
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    async onSearch(options) {
      if (options.itemsPerPage === undefined) return;
      if (options.itemsPerPage) {
        this.dataOptions.results = options.itemsPerPage;
      }
      if (options.page) {
        this.dataOptions.page = options.page;
      }
      if (options.sortBy) {
        this.dataOptions.sortBy = options.sortBy[0];
      }
      if (options.sortDesc) {
        this.dataOptions.sortDesc = options.sortDesc;
      }

      if (this.dataOptions.filter !== options.filter) {
        this.dataOptions.page = 1;
      }
      this.dataOptions.filter = options.filter;

      try {
        await this.fetchInternalUserAccounts({
          id: this.selectedUser.id,
          ...this.dataOptions,
        });
      } finally {
        this.loading = false;
      }
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "Users");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No Users in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    async onDelete(users) {
      try {
        await this.deleteInternalUserAccounts({
          userId: this.selectedUser.id,
          users,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: users.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
  },
  watch: {
    internalUsers: {
      handler() {
        this.dataSet.items = this.internalUsers.map((user) => {
          return {
            ...user,
            isSelectable: user.deletable,
            location_policies_active: user.location_policies_active,
            effective_group: user.metadata ? user.metadata.effective_group : "",
            effective_policy_id: user.metadata
              ? user.metadata.effective_policy_id
              : "",
            effective_policy:
              user.policyname ?? user.metadata.effective_policy_name,
            policy_inherited:
              user.metadata && user.metadata.policy_inherited
                ? this.$t("YES")
                : this.$t("NO"),
            policy_inherited_warning:
              user.metadata && user.metadata.show_location_policy_warning
                ? this.$t(
                    "Location policies are active. The actual policy for any user inheriting the Customer Default Policy will depend on the user's location."
                  )
                : "",
          };
        });
        if (this.dataSet.items.length > 0) {
          this.showWarning = this.dataSet.items[0].location_policies_active;
        }
      },
    },
    serverTotal: {
      handler() {
        this.serverItemsLength = this.serverTotal;
      },
      immediate: true,
    },
    showWarning() {
      this.showWarning = this.dataSet.items.length
        ? this.dataSet.items[0].location_policies_active
        : false;
    },
  },
  computed: {
    ...mapGetters("internalUsers", ["internalUsers", "serverTotal"]),
    ...mapGetters("authentication", ["selectedUser"]),
  },
  async created() {
    this.setPageTitle(this.$t("Users"));
    this.setPageSubtitle([
      this.$t(
        "Users are identified through active directory integration or the WebTitan OTG agent. Once identified, users can have policies applied to them."
      ),
    ]);
    this.loading = false;
  },
};
</script>
